import type { Location, StorageKind, StoragePlace } from "@larder/shared";
import { Households } from "../components/Households";
import { StoragePlaces } from "../components/StoragePlaces";
import type { Session } from "../data/session";
import type { PushApi } from "../push/usePush";
import type { CueApi } from "../ui/feedback";

/**
 * Where the once-or-never decisions live.
 *
 * Naming a second household and attaching a real account are both real
 * features and both terrible onboarding steps: everyone pays the cost, almost
 * nobody uses them twice. Out of the first run, into here.
 *
 * Switching and adding PLACES is not here, though it once was. The top bar
 * already lists them and adds them, and it is on screen everywhere — a second
 * copy in settings was a duplicate control. RENAMING and REMOVING them is
 * here, because the top bar is a switcher and those are decisions about a
 * place, not a choice between them — and because every account is handed a
 * "Home" it never asked for, which is the one most people want to rename.
 */
export function Settings({
  session,
  onSignOut,
  households,
  currentHousehold,
  onRenameHousehold,
  onRemoveHousehold,
  countHouseholdItems,
  places,
  onAddStorage,
  onRemoveStorage,
  busy,
  error,
  installed,
  onInstall,
  push,
  cues,
}: {
  session: Session;
  onSignOut: () => void;
  households: Location[];
  currentHousehold: string | null;
  onRenameHousehold: (id: string, name: string) => void;
  onRemoveHousehold: (id: string) => void;
  countHouseholdItems: (id: string) => Promise<number>;
  places: StoragePlace[];
  onAddStorage: (name: string, kind: StorageKind) => void;
  onRemoveStorage: (id: string) => void;
  busy: boolean;
  error: string | null;
  /** Running from the home screen, dock or launcher rather than a tab. */
  installed: boolean;
  /** Re-opens the install step. */
  onInstall: () => void;
  push: PushApi;
  cues: CueApi;
}) {
  // An account has an email or a phone number, never neither — but which one
  // depends on how they signed in, so show whichever they'd recognise.
  const who =
    session.state === "signed-in"
      ? (session.user.email ?? session.user.phoneNumber)
      : null;

  // No page title. "You" named the tab that used to open this screen, and with
  // that tab gone it was labelling nothing — the top bar is already overhead,
  // and a second heading above the first real section only pushed the content
  // down. The section keeps its accessible name.
  return (
    <section aria-label="settings">
      <h3>Your account</h3>
      <div className="panel">
        <div className="row">
          <span className="label">Signed in</span>
          <span className="value ok" data-testid="account-email">
            {who ?? "Your account"}
          </span>
        </div>
      </div>

      {/* The permanent door. Someone who has said "not now" all week should
          still be able to find this, and someone who HAS installed it should be
          told so — the alternative is an app that keeps asking with no way to
          check whether it worked. */}
      <h3>This app</h3>
      <div className="panel">
        {installed ? (
          <div className="row">
            <span className="label">Home screen</span>
            <span className="value ok" data-testid="install-state">
              Installed
            </span>
          </div>
        ) : (
          <button
            type="button"
            className="row rowbutton"
            onClick={onInstall}
            data-testid="install-open"
          >
            <span className="rowbutton-text">
              <strong>Add to home screen</strong>
              <span className="rowbutton-sub">
                Reminders only reach an installed app.
              </span>
            </span>
            <span className="rowbutton-go" aria-hidden="true">
              →
            </span>
          </button>
        )}
      </div>

      <Reminders push={push} installed={installed} />

      <Feel cues={cues} />

      <Households
        places={households}
        currentId={currentHousehold}
        uid={session.state === "signed-in" ? session.uid : null}
        onRename={onRenameHousehold}
        onRemove={onRemoveHousehold}
        countItems={countHouseholdItems}
        busy={busy}
      />

      <StoragePlaces
        places={places}
        onAdd={onAddStorage}
        onRemove={onRemoveStorage}
        busy={busy}
      />

      {error && <p className="hint">{error}</p>}

      <button
        type="button"
        className="signout"
        onClick={onSignOut}
        disabled={busy}
        data-testid="sign-out"
      >
        Sign out
      </button>
    </section>
  );
}

/**
 * The mute button, which the sound layer is not allowed to ship without.
 *
 * One switch for both halves. They are one feature to the person holding the
 * phone, and a "sound off" that still buzzed in their hand would read as a bug
 * rather than as a nuance. Per device, not per household — see ui/cues.ts.
 *
 * Phrased as what it does rather than as a state ("Turn these off", not a
 * toggle labelled Sound), because the row above it already says which way it
 * is set, and the two together are readable at a glance in a way a lone switch
 * is not.
 */
function Feel({ cues }: { cues: CueApi }) {
  return (
    <>
      <h3>Sound and vibration</h3>
      <div className="panel">
        <div className="row">
          <span className="label">Taps and chimes</span>
          <span className={cues.enabled ? "value ok" : "value"} data-testid="cues-state">
            {cues.enabled ? "On" : "Off"}
          </span>
        </div>
        <button
          type="button"
          className="row rowbutton"
          onClick={() => cues.setEnabled(!cues.enabled)}
          data-testid="cues-toggle"
        >
          <span className="rowbutton-text">
            <strong>{cues.enabled ? "Turn them off" : "Turn them on"}</strong>
            <span className="rowbutton-sub">
              {cues.enabled
                ? "Nothing on screen changes — every cue is decoration on something you can already see."
                : "A short sound and a light buzz when you answer a card or add something."}
            </span>
          </span>
        </button>
      </div>
    </>
  );
}

/**
 * The reminder switch, and the only place that can explain a blocked browser.
 *
 * Four states, and the fourth is why this section is not just a toggle.
 * `blocked` cannot be undone from inside the page — the browser will not show
 * its permission dialog a second time, and no API can re-request it. A toggle
 * that silently did nothing would be the worst version of this. So that state
 * gets sentences instead of a control, pointing at the one place the decision
 * actually lives.
 */
function Reminders({ push, installed }: { push: PushApi; installed: boolean }) {
  return (
    <>
      <h3>Reminders</h3>
      <div className="panel">
        {push.status === "on" && (
          <>
            <div className="row">
              <span className="label">Check-in reminders</span>
              <span className="value ok" data-testid="push-state">
                On
              </span>
            </div>
            {push.devices > 1 && (
              <div className="row">
                <span className="label">Devices</span>
                <span className="value">{push.devices}</span>
              </div>
            )}
            <button
              type="button"
              className="row rowbutton"
              onClick={() => void push.disable()}
              data-testid="push-disable"
            >
              <span className="rowbutton-text">
                <strong>Turn off on this device</strong>
                <span className="rowbutton-sub">
                  The in-app check-in list keeps working either way.
                </span>
              </span>
            </button>
          </>
        )}

        {push.status === "off" && (
          <button
            type="button"
            className="row rowbutton"
            onClick={() => void push.enable()}
            data-testid="push-enable"
          >
            <span className="rowbutton-text">
              <strong>Remind me before things go off</strong>
              <span className="rowbutton-sub">
                One notification, two days ahead. Nothing else.
              </span>
            </span>
            <span className="rowbutton-go" aria-hidden="true">
              →
            </span>
          </button>
        )}

        {push.status === "blocked" && (
          <div className="row rowbutton" data-testid="push-blocked">
            <span className="rowbutton-text">
              <strong>Notifications are blocked</strong>
              <span className="rowbutton-sub">
                Your browser is refusing them and only it can undo that — open the padlock
                or ⓘ beside the address bar, find Notifications, and set it to Allow.
              </span>
            </span>
          </div>
        )}

        {/* On iOS this is not a browser setting at all: a tab has no push, full
            stop, and the fix is the install step rather than any permission. */}
        {push.status === "unsupported" && (
          <div className="row rowbutton" data-testid="push-unsupported">
            <span className="rowbutton-text">
              <strong>Reminders aren't available here</strong>
              <span className="rowbutton-sub">
                {installed
                  ? "This browser can't receive them. Your check-in list still works every time you open Larder."
                  : "Add Larder to your home screen first — a browser tab can't receive reminders."}
              </span>
            </span>
          </div>
        )}
      </div>
    </>
  );
}
